import { ParrotLinkParams, PublicSetting } from '@insurgrid/common';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { useCustomLink } from './useCustomLink';
import { owlClient } from '../../apiUtils/client';
import { QueryKey } from '../../types';

export function useAgentInfo() {
  // const params = useParams<{ agentId: string }>();
  const params = useParams();
  const agentSlug = params?.agentSlug;
  const query = useSearchParams();
  const queryParamShowAcceleratedUnderwriting = query?.get(
    ParrotLinkParams.ShowAcceleratedUnderwritingQuestions,
  );
  const { customLink } = useCustomLink();
  const [showAcceleratedUnderwriting, setShowAcceleratedUnderwriting] =
    useState<string | null>(null);

  useEffect(() => {
    // set initial state from local storage

    const acceleratedUnderwritingInStorage = window.localStorage.getItem(
      ParrotLinkParams.ShowAcceleratedUnderwritingQuestions,
    );
    if (acceleratedUnderwritingInStorage) {
      setShowAcceleratedUnderwriting(acceleratedUnderwritingInStorage);
    }
  }, []);

  useEffect(() => {
    if (
      // if there is an accelerated underwriting query param
      queryParamShowAcceleratedUnderwriting &&
      // that differs from the current showAU
      showAcceleratedUnderwriting !== queryParamShowAcceleratedUnderwriting
    ) {
      // update state to query
      setShowAcceleratedUnderwriting(queryParamShowAcceleratedUnderwriting);
      // update storage to query
      window.localStorage.setItem(
        ParrotLinkParams.ShowAcceleratedUnderwritingQuestions,
        queryParamShowAcceleratedUnderwriting,
      );
    }
  }, [queryParamShowAcceleratedUnderwriting, showAcceleratedUnderwriting]);

  const {
    data: agentInfo,
    isLoading: agentLoading,
    isError: agentError,
  } = useQuery(
    [QueryKey.AgentInfo],
    () => owlClient.agent.fetch({ args: { slug: agentSlug as string } }),
    { enabled: !!agentSlug, select: (data) => data.data },
  );

  const agentHasAcceleratedUnderwritingEnabled =
    !!agentInfo?.publicSettings.find(
      (setting) => setting.key === PublicSetting.ShowAcceleratedUnderwriting,
    )?.value;

  const shouldDisplayAcceleratedUnderwritingQuestions =
    // if au preference exists
    showAcceleratedUnderwriting !== null
      ? // use the preference
        showAcceleratedUnderwriting === 'true'
      : // use the agent default setting
        agentHasAcceleratedUnderwritingEnabled;

  // if the askDrivers query isn't present, default to the public setting
  const showDisplayDriversQuestions =
    customLink?.askClientDriver ??
    shouldDisplayAcceleratedUnderwritingQuestions;

  // if the askProperty query isn't present, default to the public setting
  const showDisplayPropertyQuestions =
    customLink?.askClientProperty ??
    shouldDisplayAcceleratedUnderwritingQuestions;

  const showBeagleOffer = !!agentInfo?.publicSettings.find(
    (setting) => setting.key === PublicSetting.ShowBeagleOffer,
  )?.value;

  return {
    agentInfo,
    agentLoading,
    agentError,
    showDisplayPropertyQuestions,
    showDisplayDriversQuestions,
    showAcceleratedUnderwriting:
      showDisplayPropertyQuestions || showDisplayDriversQuestions,
    showBeagleOffer,
  };
}
